import { Link } from 'react-router-dom';
import { Bot, User, Plug, Code, Cpu } from 'lucide-react';
import { MarketplaceEntity, EntityType } from '../../lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface EntityCardProps {
  entity: MarketplaceEntity;
}

/**
 * Get icon component for entity type
 */
function getEntityTypeIcon(type: EntityType) {
  switch (type) {
    case 'prompt':
      return Bot;
    case 'specialized-model':
      return Cpu;
    case 'human':
      return User;
    case 'integration':
      return Plug;
    case 'code-block':
      return Code;
    default:
      return Bot;
  }
}

/**
 * Get display label for entity type
 */
function getEntityTypeLabel(type: EntityType): string {
  switch (type) {
    case 'prompt':
      return 'AI Prompt';
    case 'specialized-model':
      return 'Model';
    case 'human':
      return 'Human';
    case 'integration':
      return 'Integration';
    case 'code-block':
      return 'Code';
    default:
      return type;
  }
}

/**
 * Get color class for entity type badge
 */
function getEntityTypeColorClass(type: EntityType): string {
  switch (type) {
    case 'prompt':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    case 'specialized-model':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
    case 'human':
      return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    case 'integration':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
    case 'code-block':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    default:
      return '';
  }
}

export function EntityCard({ entity }: EntityCardProps) {
  const pricingLabel =
    entity.pricing.model === 'free'
      ? 'Free'
      : entity.pricing.model === 'fixed' && entity.pricing.priceCents
        ? `$${(entity.pricing.priceCents / 100).toFixed(2)}`
        : 'Variable';

  const TypeIcon = getEntityTypeIcon(entity.type);
  const typeLabel = getEntityTypeLabel(entity.type);
  const typeColorClass = getEntityTypeColorClass(entity.type);

  return (
    <Link to={`/marketplace/entities/${entity.entityId}`} className="block h-full">
      <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
        {/* Entity type header with icon */}
        <div className={`px-4 py-3 flex items-center gap-2 border-b ${typeColorClass}`}>
          <TypeIcon className="h-5 w-5" />
          <span className="text-sm font-medium">{typeLabel}</span>
        </div>

        <CardContent className="p-4 flex flex-col flex-1">
          <h3 className="font-semibold text-foreground line-clamp-2 mb-2">
            {entity.name}
          </h3>

          <p className="text-sm text-muted-foreground line-clamp-3 mb-3 min-h-[3.75rem]">
            {entity.description}
          </p>

          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2 mt-auto">
            {entity.categoryName && (
              <Badge variant="outline">{entity.categoryName}</Badge>
            )}
            <div className="flex items-center gap-3">
              <span title="Fias Fitness Score">
                FFS: {(entity.fitnessScore * 100).toFixed(0)}%
              </span>
              {entity.avgLatencyMs && (
                <span title="Average Latency">
                  {entity.avgLatencyMs < 1000
                    ? `${entity.avgLatencyMs}ms`
                    : `${(entity.avgLatencyMs / 1000).toFixed(1)}s`}
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">
              {entity.invocationCount.toLocaleString()} uses
            </span>
            <Badge variant={entity.pricing.model === 'free' ? 'secondary' : 'default'}>
              {pricingLabel}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
